<template>
  <div v-if="loading" class="bg-white rounded">
    <v-skeleton-loader type="subtitle" width="40%" class="mb-4 bg-transparent"></v-skeleton-loader>
    <v-skeleton-loader class="mx-4" type="divider"></v-skeleton-loader>
    <v-skeleton-loader type="paragraph@2" class="bg-transparent"></v-skeleton-loader>
  </div>

  <v-card
    v-else
    :data-testid="`coro-${kebabCase(widgetType)}`"
    :class="{ 'coro-widget--sidebar-open': isAnySidebarOpen }"
  >
    <v-card-title id="widget-card-title-slot" ref="cardHeader">
      <slot name="header"></slot>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text id="widget-card-text-slot" ref="cardContent">
      <slot name="content"></slot>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import kebabCase from "lodash/kebabCase";
import { defineComponent } from "vue";
import { storeToRefs } from "pinia";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";

export default defineComponent({
  props: {
    widgetType: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const { isAnySidebarOpen } = storeToRefs(useSidebarPanelStore());

    return {
      kebabCase,
      isAnySidebarOpen,
    };
  },
});
</script>

<style scoped lang="scss">
@use "./mixins";
.v-card {
  height: inherit;
  display: flex !important;
  flex-direction: column;
}

/* When a sidebar (Setup Hub / AI Assistant) is open the dashboard content area shrinks.
   Stack the widget tiles vertically regardless of viewport width so the vulnerabilities
   tile drops below the numbers/chart instead of being squeezed side by side. */
.v-card.coro-widget--sidebar-open {
  :deep(.coro-widget) {
    @include mixins.column-alignment(false);
  }
}

.v-card-title {
  height: 52px;
  padding-left: 24px !important;
  padding-right: 24px !important;
}

.v-card-text {
  flex-grow: 1;
  overflow: hidden;
  padding: 0 !important;
}

.v-skeleton-loader {
  height: inherit;
}

:deep(*) {
  .coro-widget-number {
    font-size: 48px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.05;
    flex-grow: 0;
    letter-spacing: -0.6px;
    color: rgb(var(--v-theme-primary));
    font-family: Quicksand, sans-serif;
  }

  .coro-widget--empty {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 24px 16px 24px !important;
  }

  .v-btn__content {
    font-size: 12px !important;
  }

  .v-btn:not(.v-btn--round).v-size--small {
    padding: 0 7px !important;
  }

  .coro-widget {
    &--active-vulnerability-circle {
      background-color: rgb(var(--v-theme-red-base));
      border-color: rgb(var(--v-theme-red-base));
    }
    &--resolved-vulnerability-circle {
      background-color: rgb(var(--v-theme-green-light));
      border-color: rgb(var(--v-theme-green-light));
    }
  }

  .coro-widget {
    @include mixins.row-alignment(35%, 65%);
  }

  @media only screen and (max-width: 650px) {
    .coro-widget {
      @include mixins.column-alignment(true);
    }
  }
  .v-slide-group__prev {
    display: none !important;
  }

  .coro-widget {
    &-xs {
      .v-tab {
        margin-right: 10px !important;
      }
    }
  }

  .v-table {
    thead tr th {
      border-bottom: 5px solid rgb(var(--v-theme-white)) !important;
    }

    td {
      padding: unset !important;
      border: unset !important;
      a {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }

    th:first-child,
    td:first-child {
      padding-left: 24px !important;
    }
    th:last-child,
    td:last-child {
      padding-right: 24px !important;
      width: 25%;
    }

    th {
      height: unset !important;
      margin-bottom: 10px;
    }
    td {
      height: 44px !important;
      .mdi-check:before {
        color: rgb(var(--v-theme-gray-pale)) !important;
        font-size: 17px;
      }
    }
  }

  .empty-data-icon {
    font-size: 56px !important;
  }

  .empty-data-icon:before {
    color: rgb(var(--v-theme-indigo-light)) !important;
  }

  .empty-tile-icon {
    font-size: 80px !important;
  }
}
</style>
